import { useEffect, type ReactNode } from 'react';

import {
  careStateNoteHint,
  cortexComposerHint,
  dashboardKarmaPillHint,
  dashboardPatientLocationHint,
  dashboardPatientRelationshipHint,
  dashboardPatientRequestsHint,
  dashboardPatientViewerHint,
  dashboardRoutineTabHint,
  dashboardUrgentTabHint,
  openRequestHint,
  ordersChipHint,
  patientChatHistoryHint,
  patientSnapshotHint,
  proposedActionHint,
  workspaceTabsHint,
} from '@/app/hints';
import { registerHint, unregisterHint, type Hint } from '@/shared/stores';

interface HintProviderProps {
  readonly children: ReactNode;
}

// Listed priority-descending for readability; runtime sorts by `priority` regardless.
// Dashboard hints (priority 40–70) sit below the patient-workspace set (80–100)
// so workspace coachmarks always preempt the dashboard onboarding sequence.
const HINTS: readonly Hint[] = [
  openRequestHint,
  careStateNoteHint,
  proposedActionHint,
  workspaceTabsHint,
  patientSnapshotHint,
  patientChatHistoryHint,
  cortexComposerHint,
  ordersChipHint,
  dashboardUrgentTabHint,
  dashboardRoutineTabHint,
  dashboardKarmaPillHint,
  dashboardPatientRelationshipHint,
  dashboardPatientViewerHint,
  dashboardPatientLocationHint,
  dashboardPatientRequestsHint,
];

/**
 * Registers app-level hints for the provider's lifetime. Router-agnostic
 * (eligibility = anchor mount + dismissal), so it can sit anywhere in the tree.
 */
export function HintProvider({ children }: HintProviderProps) {
  useEffect(() => {
    HINTS.forEach(registerHint);
    return () => {
      HINTS.forEach((h) => {
        unregisterHint(h.id);
      });
    };
  }, []);

  return <>{children}</>;
}
